// src/ui/components/profileModal.ts — Profile Switcher & Sandbox Modal (FR-PROF-001, FR-PROF-002)
// Allows parents and evaluators to switch between Tudor's authentic learning record
// and the isolated Tester Sandbox with zero metric pollution and one-click data purging.

import { ProfileManager, UserProfile } from '../../storage/profileManager';
import { renderCornerFiligree } from './ornateHelpers';

export class ProfileModal {
  private container: HTMLElement;
  private keydownHandler?: (e: KeyboardEvent) => void;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'profile-switcher-modal';
    this.container.setAttribute('role', 'dialog');
    this.container.setAttribute('aria-modal', 'true');
    this.container.setAttribute('aria-label', 'Learner Profile & Sandbox Settings');
    this.container.style.cssText = `
      position: fixed;
      inset: 0;
      background: radial-gradient(circle at center, rgba(10, 25, 47, 0.88), rgba(4, 10, 22, 0.95));
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 2500;
      padding: 20px;
      animation: fadeIn 0.3s ease;
      touch-action: manipulation;
    `;

    this.render();
    this.attachEvents();
  }

  getElement(): HTMLElement {
    return this.container;
  }

  show(): void {
    document.body.appendChild(this.container);
  }

  close(): void {
    if (this.keydownHandler) {
      window.removeEventListener('keydown', this.keydownHandler);
    }
    this.container.remove();
  }

  private render(): void {
    const active = ProfileManager.getActiveProfile();
    const sandboxCount = ProfileManager.getTesterKeyCount();

    this.container.innerHTML = `
      <div class="vellum-surface" style="
        width: 100%;
        max-width: 580px;
        position: relative;
        padding: 30px 28px;
        border-radius: 18px;
        box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5), inset 0 0 40px rgba(229, 169, 60, 0.08);
      ">
        ${renderCornerFiligree()}

        <!-- Header -->
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 22px; position: relative; z-index: 2;">
          <div>
            <div class="atelier-eyebrow-pill">
              <span>👤</span>
              <span>LEARNER PROFILES</span>
            </div>
            <h2 style="font-family: var(--font-heading); font-size: 1.6rem; color: var(--morris-indigo); margin: 6px 0 0 0;">
              Switch Active Profile
            </h2>
          </div>
          <button id="profile-modal-close" class="touch-target btn-vellum" style="min-width: 44px; width: 44px; height: 44px; border-radius: 50%; padding: 0; font-size: 1.25rem; display: flex; align-items: center; justify-content: center; cursor: pointer;" aria-label="Close Profile Dialog">
            &times;
          </button>
        </div>

        <!-- Description -->
        <p style="font-size: 0.95rem; line-height: 1.5; color: var(--color-ink-muted, #4A5568); margin-bottom: 24px; position: relative; z-index: 2;">
          Choose whether you are running the atelier as Tudor (recording genuine Bexley 11+ readiness and daily streaks) or in an isolated Tester Sandbox.
        </p>

        <!-- Profile Selection Cards -->
        <div style="display: flex; flex-direction: column; gap: 14px; margin-bottom: 24px; position: relative; z-index: 2;">
          <!-- Tudor Profile -->
          <div id="profile-card-tudor" class="card-interactive" style="
            padding: 16px 20px;
            border-radius: 12px;
            background: ${active === 'tudor' ? 'linear-gradient(180deg, #FFFDF9 0%, #F5EBCE 100%)' : '#FAF6ED'};
            border: 2px solid ${active === 'tudor' ? 'var(--gilded-brass)' : 'rgba(213, 197, 163, 0.8)'};
            box-shadow: ${active === 'tudor' ? '0 4px 14px rgba(229, 169, 60, 0.28)' : 'none'};
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
          ">
            <div>
              <div style="display: flex; align-items: center; gap: 8px;">
                <span style="font-size: 1.3rem;">⭐</span>
                <span style="font-family: var(--font-heading); font-weight: 700; font-size: 1.15rem; color: var(--morris-indigo);">
                  Tudor (Year 4 Candidate)
                </span>
                ${active === 'tudor' ? '<span class="domain-tag" style="background: rgba(30, 90, 60, 0.15); color: #1E5A3C; border-color: #1E5A3C;">ACTIVE RECORD</span>' : ''}
              </div>
              <p style="font-size: 0.85rem; color: #4A5568; margin: 4px 0 0 0; line-height: 1.4;">
                Primary learner profile. Saves official Quest SAS scores, 4-axis radar, 48-hr streak state, and cognitive fatigue curves.
              </p>
            </div>
            <button class="touch-target ${active === 'tudor' ? 'btn-brass' : 'btn-vellum'}" style="flex-shrink: 0; min-height: 44px; padding: 6px 14px; font-size: 0.9rem;" ${active === 'tudor' ? 'disabled' : ''}>
              ${active === 'tudor' ? 'Selected' : 'Switch'}
            </button>
          </div>

          <!-- Tester Sandbox Profile -->
          <div id="profile-card-tester" class="card-interactive" style="
            padding: 16px 20px;
            border-radius: 12px;
            background: ${active === 'tester' ? 'linear-gradient(180deg, #F0F9FF 0%, #E0F2FE 100%)' : '#FAF6ED'};
            border: 2px solid ${active === 'tester' ? '#0284C7' : 'rgba(213, 197, 163, 0.8)'};
            box-shadow: ${active === 'tester' ? '0 4px 14px rgba(2, 132, 199, 0.25)' : 'none'};
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
          ">
            <div>
              <div style="display: flex; align-items: center; gap: 8px;">
                <span style="font-size: 1.3rem;">🧪</span>
                <span style="font-family: var(--font-heading); font-weight: 700; font-size: 1.15rem; color: var(--morris-indigo);">
                  Tester Sandbox (Evaluator Mode)
                </span>
                ${active === 'tester' ? '<span class="domain-tag" style="background: rgba(2, 132, 199, 0.15); color: #0369A1; border-color: #0284C7;">ACTIVE SANDBOX</span>' : ''}
              </div>
              <p style="font-size: 0.85rem; color: #4A5568; margin: 4px 0 0 0; line-height: 1.4;">
                Isolated sandbox for Florin / testers. Try any station, timer, hint, or mock without polluting Tudor's statistics.
              </p>
            </div>
            <button class="touch-target ${active === 'tester' ? 'btn-coral' : 'btn-vellum'}" style="flex-shrink: 0; min-height: 44px; padding: 6px 14px; font-size: 0.9rem;" ${active === 'tester' ? 'disabled' : ''}>
              ${active === 'tester' ? 'Selected' : 'Switch'}
            </button>
          </div>
        </div>

        <!-- Sandbox Purge Section -->
        <div style="
          padding: 14px 18px;
          background: rgba(26, 54, 93, 0.05);
          border: 1px dashed var(--vellum-edge);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          position: relative;
          z-index: 2;
        ">
          <div>
            <div style="font-weight: 700; font-size: 0.9rem; color: var(--morris-indigo);">
              Sandbox Isolation Status
            </div>
            <div id="sandbox-item-count" style="font-size: 0.8rem; color: #555;">
              ${sandboxCount === 0 ? 'No test records stored in sandbox.' : `${sandboxCount} test records currently stored in sandbox.`}
            </div>
          </div>
          <button id="purge-sandbox-btn" class="touch-target btn-vellum" style="font-size: 0.8rem; padding: 8px 12px; border-color: #E53E3E; color: #C53030; min-height: 44px; cursor: pointer;" ${sandboxCount === 0 ? 'disabled' : ''}>
            🧹 Purge Sandbox Data
          </button>
        </div>
      </div>
    `;
  }

  private attachEvents(): void {
    // Close button
    this.container.querySelector('#profile-modal-close')?.addEventListener('click', () => {
      this.close();
    });

    // Close on clicking backdrop outside modal content
    this.container.addEventListener('click', (e) => {
      if (e.target === this.container) {
        this.close();
      }
    });

    // Switch to Tudor
    this.container.querySelector('#profile-card-tudor')?.addEventListener('click', () => {
      this.handleSelectProfile('tudor');
    });

    // Switch to Tester
    this.container.querySelector('#profile-card-tester')?.addEventListener('click', () => {
      this.handleSelectProfile('tester');
    });

    // Purge sandbox
    this.container.querySelector('#purge-sandbox-btn')?.addEventListener('click', () => {
      const removed = ProfileManager.clearTesterData();
      const countEl = this.container.querySelector<HTMLElement>('#sandbox-item-count');
      if (countEl) {
        countEl.textContent = `Cleaned! ${removed} sandbox records deleted. Tudor data unaffected.`;
        countEl.style.color = '#1E5A3C';
      }
      const purgeBtn = this.container.querySelector<HTMLButtonElement>('#purge-sandbox-btn');
      if (purgeBtn) {
        purgeBtn.disabled = true;
      }
    });

    // Escape key
    this.keydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.close();
      }
    };
    window.addEventListener('keydown', this.keydownHandler);
  }

  private handleSelectProfile(profile: UserProfile): void {
    ProfileManager.setActiveProfile(profile);
    this.render();
    this.attachEvents();
  }
}
